import React, { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TPageProps } from 'models/common.model'
import { useDelayedContinue } from 'hooks/useDelayedContinue'
import { Option, OptionType } from 'components/Option'
import { Container } from 'components/Container'
import { useNextStep } from 'hooks/useNextStep'

import { AnswerButtonTransparent } from 'components/AnswerButtonTransparent'
import { useSelector } from 'react-redux'
import { selectCurrentVariantCohort } from 'root-redux/selects/common'
import { useGetPageInfo } from 'hooks/useGetPageInfo'
import { Cohort } from 'root-constants'
import { AnswerButtonLight } from 'components/AnswerButtonLight'
import { StyledSimplePage as S } from './SimplePage.styles'
import { DefaultOptions, QUESTIONS_MAP } from './constants'

export const SimplePage: FC<TPageProps> = ({ pageId, nextPagePath }) => {
  const { t } = useTranslation()
  const { isAnswersDisabled, handleChange, setIsAnswersDisabled } =
    useDelayedContinue()
  const { hasQuestionDescription } = useGetPageInfo()

  const cohort = useSelector(selectCurrentVariantCohort)

  const handleContinue = useNextStep({
    pageId,
    question: QUESTIONS_MAP[cohort || Cohort.VARIANT_ONE][pageId],
    nextPagePath,
  })

  const isQuizCohort = useMemo(() => cohort === Cohort.VARIANT_TWO, [cohort])

  const optionProps = useMemo(
    () => ({
      name: pageId,
      type: OptionType.RADIO,
      onChange: (value: string) => {
        handleChange(value, handleContinue)
        setIsAnswersDisabled(true)
      },
    }),
    [pageId, handleChange, setIsAnswersDisabled, handleContinue],
  )

  return (
    <Container>
      <S.Title hasQuestionDescription={hasQuestionDescription}>
        {t(`onboarding.${pageId}.${cohort}.title` as any)}
      </S.Title>

      {hasQuestionDescription && (
        <S.Subtitle>
          {t(`onboarding.${pageId}.${cohort}.subtitle` as any)}
        </S.Subtitle>
      )}

      {DefaultOptions.map((option) => (
        <Option
          {...optionProps}
          key={option.id}
          disabled={isAnswersDisabled}
          value={option.value}
        >
          {isQuizCohort ? (
            <AnswerButtonLight>{t(option.key as any)}</AnswerButtonLight>
          ) : (
            <AnswerButtonTransparent>
              {t(option.key as any)}
            </AnswerButtonTransparent>
          )}
        </Option>
      ))}
    </Container>
  )
}
